import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Flex, Modal, Button, RadioItem } from 'ui';
import { LoadingOutlined } from '@ant-design/icons';
// eslint-disable-next-line
import ReferenceDataDropdown from 'common/reference-data/containers/reference-data-dropdown-container';
import { Row, Col, Radio, DatePicker, Checkbox } from 'antd';
import moment from 'utils/cops-moment';
import { planningActions, orderActions } from '../../../workgrid/helpers/grid-helpers';
import useLocalStorage from '../../../utils/hooks/useLocalStorage';
import { multipleProcessQuickActions } from '../../../utils/app-configs';

const StyledRow = styled(Row)`
  margin-top: 20px;
  line-height: normal;
`;

const StyledPlanningRowOption = styled(Row)`
  margin-top: 15px;
  margin-left: 25px;
`;

const CheckboxWrapper = styled.div`
  margin-top: 10px;
  display: block;
`;

const OptionCol = styled(Col)`
  margin-top: 6px;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 200px;
`;

const StyledRadio = styled(RadioItem)`
  display: block;
  height: 30px;
  line-height: 30px;
`;

const StyledRadioSequentialRowOption = styled(RadioItem)`
  display: block;
  height: 20px;
  line-height: 10px;
`;

const StyledButton = styled(Button)`
  margin-right: 12px;
`;

const IconContainer = styled(Flex)`
  width: 20px;
  height: 20px;
  margin-right: 5px;
  display: inline-block;
`;

const ProcessIcon = styled(LoadingOutlined)`
  font-size: 15px;
  color: ${p => p.theme.colors.publishSteps.process} !important;
`;

const StepsWrapper = styled.div`
  opacity: 0.5;
  display: ${p => p.display ? ' block' : 'none'};
`;

const StartDateDialog = ({
  selectedActions,
  visible,
  onClose,
  onSubmit,
  intl,
  needsTime,
  needsWorkcenter,
  serviceCenterId,
  loading,
  isFullScreen,
  allowPast = false,
  multipleProcess = null,
  changeMultipleProcess = null,
}) => {
  const [keepOriginal, setKeepOriginal] = useState(true);
  const [isSequential, setIsSequential] = useState(true);
  const [toStatus, setToStatus] = useState(1);
  const [sendToSap, setSendToSap] = useLocalStorage(
    'publishMoveToOrders',
    false,
  );
  const [keepOriginalWorkcenter, setKeepOriginalWorkcenter] = useState(true);
  const [newDate, setNewDate] = useState(null);
  // eslint-disable-next-line
  const [technicianNumber, setTechnicianNumber] = useState(undefined);
  // eslint-disable-next-line
  const [technicianId, setTechnicianId] = useState(undefined);

  // we show a different UI for actions updateOrderBackground(order) and
  // updatePlanningBackground(planning)
  const showMoveUpdateDialog = selectedActions && selectedActions?.length ?
    selectedActions[0] === orderActions[7] || selectedActions[0] === planningActions[4] :
    null;

  useEffect(() => {
    if (visible) {
      setKeepOriginal(true);
      setKeepOriginalWorkcenter(true);
      setNewDate(null);
      // eslint-disable-next-line
      setTechnicianNumber(undefined);
      // eslint-disable-next-line
      setTechnicianId(undefined);
    }
  }, [visible]);

  const onEngineerChange = (engineer) => {
    if (keepOriginalWorkcenter && engineer) {
      setKeepOriginalWorkcenter(false);
      setTechnicianNumber(engineer.number);
      setTechnicianId(engineer.id);
    } else {
      setTechnicianNumber(engineer && engineer.number);
      setTechnicianId(engineer && engineer.id);
    }
  };

  const save = () => {
    if (changeMultipleProcess) {
      changeMultipleProcess();
    }
    onSubmit(
      keepOriginal ? null : newDate.format('YYYY-MM-DDTHH:mm'),
      keepOriginalWorkcenter ? null : technicianId,
      // if we show move-update dialog & we edit the start date
      // we need the isSequential flag, if not default it to null
      showMoveUpdateDialog && !keepOriginal ? isSequential : null,
      toStatus,
      sendToSap,
    );
  };

  const showProcesses = () => <StepsWrapper display={!!multipleProcess && sendToSap}>
    <IconContainer jcc aic>
      <ProcessIcon size='xs' />
    </IconContainer>
    {intl.formatMessage({
      id: multipleProcess === multipleProcessQuickActions.processingOrders &&
        selectedActions?.includes('updateOrderBackground') ?
        'toolbar.startDate.STEPS_PROCESSING_ORDERS' : 'toolbar.startDate.STEPS_PUBLISH_TO_SAP',
    })}
  </StepsWrapper>;

  const transformValues = values => values?.filter(v => !v.isRegion);

  const disabled = (!keepOriginal && !newDate) || (!keepOriginalWorkcenter && !technicianNumber) ||
    (showMoveUpdateDialog && !technicianNumber && !technicianId);

  const workcenterDropdown = showMoveUpdateDialog ?
    <StyledRow>
      <Col md={6}>
        {intl.formatMessage({
          id: 'toolbar.startDate.MOVE_PLANNING_TO_FSE',
        })}
      </Col>
      <ReferenceDataDropdown
        width={'225px'}
        isSorted={true}
        placeholder={intl.formatMessage({ id: 'toolbar.startDate.SELECT_ENGINEER' })}
        type='order'
        name='workCenters'
        valueKey='number'
        valueLabel='name'
        value={technicianNumber}
        onObjectChanged={onEngineerChange}
        transformValues={transformValues}
        serviceCenterId={serviceCenterId}
      />
    </StyledRow>
    :
    <StyledRow>
      <OptionCol md={6}>
        {intl.formatMessage({
          id: 'toolbar.startDate.ASSIGN_ENGINEER',
        })}
      </OptionCol>
      <Col md={18}>
        <Radio.Group
          onChange={e => setKeepOriginalWorkcenter(e.target.value)}
          value={keepOriginalWorkcenter}
        >
          <StyledRadio value={true}>
            {intl.formatMessage({
              id: 'toolbar.startDate.KEEP_ORIGINAL_WORKCENTER',
            })}
          </StyledRadio>
          <StyledRadio value={false} preventPropagation={true}>
            <ReferenceDataDropdown
              width={'200px'}
              isSorted={true}
              placeholder={intl.formatMessage({ id: 'toolbar.startDate.SELECT_ENGINEER' })}
              type='order'
              name='workCenters'
              valueKey='number'
              valueLabel='name'
              value={technicianNumber}
              onObjectChanged={onEngineerChange}
              transformValues={transformValues}
              serviceCenterId={serviceCenterId}
            />
          </StyledRadio>
        </Radio.Group>
      </Col>
    </StyledRow>;

  const showMoveToOptionsOrders = () =>
    showMoveUpdateDialog && selectedActions?.includes('updateOrderBackground') &&
    <Row>
      <OptionCol md={6}>
        {intl.formatMessage({
          id: 'toolbar.startDate.MOVE_STATUS_TO',
        })}
      </OptionCol>
      <Col md={18}>
        <Radio.Group
          onChange={e => setToStatus(e.target.value)}
          value={toStatus}
        >
          <StyledRadio value={1}>
            {intl.formatMessage({
              id: 'toolbar.startDate.TENTATIVE',
            })}
          </StyledRadio>
          <StyledRadio value={2}>
            {intl.formatMessage({
              id: 'toolbar.startDate.CONFIRMED',
            })}
          </StyledRadio>
        </Radio.Group>
      </Col>
      <Col md={18} offset={6}>
        <CheckboxWrapper>
          <Checkbox
            checked={sendToSap}
            onChange={(e) => {
              setSendToSap(e.target.checked);
            }}
          >{intl.formatMessage({
            id: 'toolbar.startDate.PUBLISH_TO_SAP',
          })}</Checkbox>
        </CheckboxWrapper>
      </Col>
      <Col>
        {showProcesses()}
      </Col>
    </Row>;

  return (
    <Modal
      title={intl.formatMessage({ id: 'dateDialog.CHOOSE_START_DATE' })}
      open={visible}
      footer={null}
      onCancel={onClose}
      destroyOnClose={true}
      getContainer={() =>
        isFullScreen ? document.getElementsByClassName('fullscreen-enabled')[0] : document.body
      }
    >
      {showMoveUpdateDialog &&
        workcenterDropdown
      }
      <Row>
        <OptionCol md={6}>
          {intl.formatMessage({
            id: 'toolbar.startDate.SET_START_DATE',
          })}
        </OptionCol>
        <Col md={18}>
          <Radio.Group
            onChange={e => setKeepOriginal(e.target.value)}
            value={keepOriginal}
          >
            <StyledRadio value={true}>
              {intl.formatMessage({
                id: showMoveUpdateDialog ?
                  'toolbar.startDate.MOVE_CURRENT_PLANNING' : 'toolbar.startDate.KEEP_ORIGINAL',
              })}
            </StyledRadio>
            <StyledRadio value={false} preventPropagation={true}>
              {needsTime ?
                <StyledDatePicker
                  format='DD-MM-YYYY HH:mm'
                  value={newDate}
                  getPopupContainer={() => document.getElementsByClassName('fullscreen')[0]}
                  placeholder={
                    intl.formatMessage({
                      id: showMoveUpdateDialog ?
                        'toolbar.startDate.MOVE_RECALCULATE' : 'toolbar.startDate.SELECT_DATE_TIME',
                    })
                  }
                  disabledDate={current => allowPast ? false : current.isBefore(
                    moment().startOf('day'),
                  )}
                  disabledTime={curr => allowPast ||
                    (!curr || curr.isAfter(moment(), 'day')) ?
                    [] :
                    {
                      disabledHours: () => Array.from(
                        { length: moment().minute() > 45 ? moment().hour() + 1 : moment().hour() },
                        (_, i) => i,
                      ),
                      disabledMinutes: selectedHour => selectedHour !== moment().hour() ?
                        [] :
                        [0, 15, 30, 45].filter(i => i < moment().minute()),
                    }}
                  showTime={{
                    allowClear: false,
                    showSecond: false,
                    format: 'HH:mm',
                    minuteStep: 15,
                    defaultValue: moment('08:00', 'HH:mm'),
                  }}
                  onChange={(date) => {
                    if (keepOriginal) {
                      setKeepOriginal(false);
                    }
                    setNewDate(date);
                  }}
                />
                :
                <StyledDatePicker
                  value={newDate}
                  onChange={(date) => {
                    if (keepOriginal) {
                      setKeepOriginal(false);
                    }
                    setNewDate(date);
                  }}
                  getPopupContainer={() => document.getElementsByClassName('fullscreen')[0]}
                  disabledDate={current => allowPast ? false : current.isBefore(
                    moment().startOf('day'),
                  )}
                />
              }
            </StyledRadio>
            {showMoveUpdateDialog && selectedActions?.length > 1 &&
              // only show this when you have more than one item selected and when you show
              // move-update dialog
              <StyledPlanningRowOption>
                <Radio.Group
                  disabled={keepOriginal}
                  onChange={e => setIsSequential(e.target.value)}
                  value={isSequential}
                >
                  <StyledRadioSequentialRowOption value={true}>
                    {intl.formatMessage({
                      id: 'toolbar.startDate.PLAN_SEQUENTIALLY',
                    })}
                  </StyledRadioSequentialRowOption>
                  <StyledRadioSequentialRowOption value={false}>
                    {intl.formatMessage({
                      id: 'toolbar.startDate.PLAN_PARALLEL',
                    })}
                  </StyledRadioSequentialRowOption>
                </Radio.Group>
              </StyledPlanningRowOption>
            }
          </Radio.Group>
        </Col>
      </Row>
      {showMoveToOptionsOrders()}
      {!showMoveUpdateDialog && needsWorkcenter &&
        workcenterDropdown
      }
      <Flex jcfe margin={10}>
        <StyledButton type='secondary' light onClick={onClose}>
          {intl.formatMessage({
            id: 'toolbar.startDate.CANCEL',
          })}
        </StyledButton>
        <Button
          type='secondary'
          onClick={save}
          disabled={disabled}
          loading={loading}
          data-test-id='btn-move-planning-ok'
        >
          {intl.formatMessage({
            id: 'toolbar.startDate.OK',
          })}
        </Button>
      </Flex>
    </Modal>
  );
};

StartDateDialog.propTypes = {
  selectedActions: PropTypes.array,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  intl: PropTypes.object,
  needsTime: PropTypes.bool,
  needsWorkcenter: PropTypes.bool,
  serviceCenterId: PropTypes.string,
  loading: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  allowPast: PropTypes.bool,
  multipleProcess: PropTypes.string,
  changeMultipleProcess: PropTypes.func,
};

export default injectIntl(StartDateDialog);
